import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import type { SaveStatus } from "../hooks";
import type { MissingItem, Progress, SectionProgress } from "../types";

/**
 * « Portail/Barre supérieure » (Figma `426:15215`) — the dossier's name and its save state. Same
 * indicator either way; only the left side changes.
 *
 * Two things only, and the emptiness is the point: the applicant is mid-answer, and the chrome
 * should not compete with the question in front of them.
 *
 * The design note on this component is emphatic about the indicator: *visible permanently, not only
 * on failure, and never optimistic.* It also insists « Échec » (a write that failed) and « hors
 * ligne » (no network) are **not interchangeable** — different cause, different thing for the
 * applicant to do — which is why `SaveStatus` carries both.
 *
 * `onBack` switches this to the « Portail/Barre supérieure — retour » variant (Figma `432:20823`)
 * — used ONLY by the review screen. Its own component description is explicit about why: review
 * "is not a stepper tab, it's a door you walk through once, not content you're drafting," so it
 * gets its own back (« ← Mon dossier ») instead of the tab strip the four wizard steps show below
 * this same bar. Omit the prop for the plain title, same as every wizard step already does.
 */
export function DossierTopBar({
  status,
  onBack,
}: {
  status: SaveStatus;
  onBack?: () => void;
}) {
  const { t } = useTranslation("acquire");

  return (
    <header className="border-b border-(--border-default) bg-surface-card">
      {/*
        `max-w-285` (1140px) is the desktop frame's content well (`533:33577`) — 720px form + 40px gap +
        380px side panel, centred with 150px margins at exactly 1440px. Below `xl` the viewport never
        reaches that width, so `w-full` governs and the cap is inert; no breakpoint prefix needed.
      */}
      <div className="mx-auto flex w-full max-w-285 items-center justify-between gap-sm px-md py-3.5 sm:px-xl">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 flex items-center gap-xxs rounded-md py-2 pl-1 pr-2 text-sm-semibold text-(--text-secondary) outline-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-focus-ring"
          >
            <Icon name="arrow-left" size={20} />
            {t("dossier.title")}
          </button>
        ) : (
          <p className="text-md-semibold text-(--text-primary)">
            {t("dossier.title")}
          </p>
        )}

        {status === "idle" ? null : (
          <p
            // `polite`, so a screen reader hears that the work is safe without interrupting typing.
            role="status"
            aria-live="polite"
            className="flex shrink-0 items-center gap-xxs text-sm-regular text-(--text-tertiary)"
          >
            {/*
            The GLYPH carries the state, the text stays tertiary grey — the design's treatment. A
            coloured sentence would make every save feel like an event.
          */}
            <span
              aria-hidden
              className={
                status === "saved"
                  ? "text-(--feedback-success-icon)"
                  : status === "saving"
                    ? "animate-spin text-(--text-tertiary)"
                    : "text-(--feedback-warning-icon)"
              }
            >
              <Icon
                name={
                  status === "saved"
                    ? "check"
                    : status === "saving"
                      ? "loading"
                      : "alert"
                }
                size={16}
              />
            </span>
            {t(`dossier.autosave.${status}`)}
          </p>
        )}
      </div>
    </header>
  );
}

/**
 * « Portail/Étapes » (Figma `426:15144`) — four steps, each with its own completion track.
 * « Portail/Étapes — bureau » (Figma `533:33654`) is the SAME component at 1440px, not a
 * different one — same label-over-track shape, same rule that only weight/colour marks the active
 * step. Two things change with the width, and both come straight from the desktop frame's own note:
 *
 * - the tabs stop being four EQUAL columns and become NATURAL width with a fixed 36px gap, because
 *   at 1440px there is no reason to stretch "Équipe" to match "Practical details";
 * - the missing-items disclosure disappears. `Portail/Panneau latéral — bureau`'s own note says it
 *   *"replaces two of the mobile layout's hiding gestures — the Documents sheet and the
 *   « N éléments manquants » link"*: on a wide screen there is room to keep that list permanently
 *   visible in the side panel, so the tap-to-reveal it exists for on a phone has nothing left to do.
 *
 * ## The tracks are the server's numbers, never ours
 *
 * `progress.sections[].complete_percent` is recomputed by the same engine that decides whether the
 * dossier may be submitted. The component note: the track *"never advances backwards and never
 * reaches 100% while a required item is missing"* — true only because it is not derived here.
 * US-E3-01: a bar that says 90% with something required missing teaches applicants not to trust it.
 *
 * ## The count names what is missing (mobile only — see above)
 *
 * The mobile frame's note is explicit — *« Le décompte renvoie vers des éléments NOMMÉS — un nombre
 * nu ne suffit pas »*. The review screen that would normally host that list is not built, so on
 * narrow viewports the count is a disclosure that expands the named items inline from
 * `missing_items[]`. A bare number would not satisfy the story.
 *
 * ## Deviation to close later
 *
 * ⚠️ The design says all four tabs are *« librement cliquables — aucun verrouillage »*, and US-E3-01
 * agrees that nothing may be locked behind completing something else. Steps 2–4 are not built, so
 * they are inert here. That is a temporary state, not the intended behaviour.
 */
export function DossierSteps({
  sections,
  progress,
  missingItems,
  activeKey,
  enabledKeys,
  onSelect,
}: {
  sections: { key: string; title: string }[];
  progress: Progress;
  missingItems: MissingItem[];
  activeKey: string;
  enabledKeys: string[];
  /** Fires for a built step's own tab — never for `activeKey` itself, never for a disabled one. */
  onSelect: (key: string) => void;
}) {
  const { t } = useTranslation("acquire");
  const [showMissing, setShowMissing] = useState(false);

  const byKey = new Map<string, SectionProgress>(
    progress.sections.map((item) => [item.key, item])
  );

  /**
   * ⚠️ DERIVED, because the payload has no overall percentage — only per-section ones and a
   * `complete` boolean. The mean of the sections is the honest approximation available.
   *
   * Capped at 99 unless the server says `complete`, which keeps US-E3-01's rule true by
   * construction: the figure can never read 100% while anything required is outstanding. The ask
   * for Aymen is an overall `complete_percent` on `progress`.
   */
  const overall = progress.complete
    ? 100
    : Math.min(
        99,
        Math.round(
          progress.sections.reduce(
            (total, item) => total + item.complete_percent,
            0
          ) / Math.max(1, progress.sections.length)
        )
      );

  /** Weight/colour only — the rule both breakpoints share; the caller decides the text size. */
  const labelTone = (section: { key: string }) =>
    section.key === activeKey
      ? "text-(--text-primary)"
      : enabledKeys.includes(section.key)
        ? "text-(--text-tertiary)"
        : "text-(--text-disabled)";

  return (
    <nav
      aria-label={t("dossier.steps.label")}
      className="border-b border-(--border-default) bg-surface-card"
    >
      {/*
        Below `xl`: equal-width wrapped columns, the completion summary, and the disclosure.
        `max-w-192` mirrors the intro/main column so the stepper lines up with the content under it
        on a tablet-width viewport instead of running the full browser width while everything below
        it is centred.
      */}
      <div className="mx-auto w-full max-w-192 xl:hidden">
        {/* Four equal columns — `flex-1` + `min-w-0`, so the labels share the width and wrap rather
            than pushing the row wider than 320px. */}
        <ol className="flex items-start gap-xs px-md pt-3.5 pb-sm sm:px-xl">
          {sections.map((section) => {
            const isActive = section.key === activeKey;
            const isEnabled = enabledKeys.includes(section.key);
            const percent = byKey.get(section.key)?.complete_percent ?? 0;

            return (
              <li
                key={section.key}
                aria-current={isActive ? "step" : undefined}
                // `justify-between` + a two-line floor keeps every track on the same baseline. The
                // labels are the SERVER's, so their length varies by locale — "Pratique" is one
                // line and "Practical details" is two, and a wrapped label must not drag its track
                // down.
                className="flex min-w-0 flex-1 flex-col justify-between gap-xs"
              >
                {/*
                  The active step is marked by WEIGHT and colour together, never colour alone — the
                  component note calls that out, and it is what keeps the stepper readable for
                  anyone who cannot separate the two. A built, non-active step is a real button —
                  the design's own note says the four tabs are freely clickable, no locking.
                */}
                <button
                  type="button"
                  disabled={isActive || !isEnabled}
                  onClick={() => onSelect(section.key)}
                  className={`min-h-9 text-left ${isActive ? "text-xs-semibold" : "text-xs-medium"} ${labelTone(section)} ${isEnabled && !isActive ? "cursor-pointer underline-offset-2 hover:underline" : ""}`}
                >
                  {section.title}
                </button>
                <span
                  // The track is decorative; the percentage is announced by the summary line below.
                  aria-hidden
                  className="h-1 w-full overflow-hidden rounded-full bg-(--border-default)"
                >
                  <span
                    className="block h-1 rounded-full bg-menthe-600"
                    style={{ width: `${String(percent)}%` }}
                  />
                </span>
              </li>
            );
          })}
        </ol>

        <div className="flex items-center justify-between gap-sm px-md pb-sm sm:px-xl">
          <p className="text-xs-medium text-(--text-secondary)">
            {t("dossier.steps.overall", { percent: overall })}
          </p>

          {progress.missing_count > 0 ? (
            <button
              type="button"
              aria-expanded={showMissing}
              onClick={() => setShowMissing((open) => !open)}
              className="shrink-0 rounded-sm text-xs-semibold text-(--text-secondary) underline outline-none hover:text-(--text-primary) focus-visible:ring-2 focus-visible:ring-focus-ring"
            >
              {t("dossier.steps.missing", { count: progress.missing_count })}
            </button>
          ) : null}
        </div>

        {/* The named items. Grouped by nothing and unlinked for now — the fields they point at
            live on steps that are not built, so a link would go nowhere. The NAMES are the point. */}
        {showMissing ? (
          <ul className="flex max-h-64 flex-col gap-xxs overflow-y-auto border-t border-(--border-default) px-md py-sm sm:px-xl">
            {missingItems.map((item) => (
              <li
                key={`${item.section}:${item.code}`}
                className="flex items-start gap-xs text-xs-regular text-(--text-secondary)"
              >
                <span
                  aria-hidden
                  className="mt-0.5 shrink-0 text-(--text-tertiary)"
                >
                  <Icon name="circle-dashed" size={14} />
                </span>
                <span className="min-w-0 flex-1 wrap-break-word">
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/*
        `xl` and up: natural-width tabs, no disclosure — the missing-items list moved to
        `DossierSidePanel`. `max-w-285` (1140px) is the same content well as the top bar, so both bars
        and the two-column body below all share one left edge.
      */}
      <div className="mx-auto hidden w-full max-w-285 items-end justify-between gap-lg pt-4.5 pb-3.75 xl:flex">
        <ol className="flex items-start gap-9">
          {sections.map((section) => {
            const isActive = section.key === activeKey;
            const isEnabled = enabledKeys.includes(section.key);
            const percent = byKey.get(section.key)?.complete_percent ?? 0;

            return (
              <li
                key={section.key}
                aria-current={isActive ? "step" : undefined}
                className="flex flex-col items-start gap-sm"
              >
                <button
                  type="button"
                  disabled={isActive || !isEnabled}
                  onClick={() => onSelect(section.key)}
                  className={`text-sm-medium whitespace-nowrap ${isActive ? "text-sm-semibold" : ""} ${labelTone(section)} ${isEnabled && !isActive ? "cursor-pointer underline-offset-2 hover:underline" : ""}`}
                >
                  {section.title}
                </button>
                {/* No fixed width: the track fills the label's own natural width, exactly as the
                    frame draws it — a flex-column item with no `w-full` sibling shrinks to its
                    widest child, and this track's `w-full` fills that same box. */}
                <span
                  aria-hidden
                  className="h-1 w-full overflow-hidden rounded-full bg-(--border-default)"
                >
                  <span
                    className="block h-1 rounded-full bg-menthe-600"
                    style={{ width: `${String(percent)}%` }}
                  />
                </span>
              </li>
            );
          })}
        </ol>

        <p className="shrink-0 text-sm-medium text-(--text-secondary)">
          {t("dossier.steps.overall", { percent: overall })}
        </p>
      </div>
    </nav>
  );
}

/**
 * « Intro — étape » — the brand-washed panel that opens a step.
 *
 * The chip states position in the sequence in words (« Étape 1 sur 4 »), which the tracks above
 * cannot: a bar shows how full a step is, not which one you are standing in.
 *
 * The gradient is the same wash as the Aperçu's round banner — the frames call it out as taken
 * from this component, so the applicant meets one visual idea on both sides of the account wall.
 */
export function StepIntro({
  index,
  total,
  title,
  description,
}: {
  index: number;
  total: number;
  title: string;
  description: string;
}) {
  const { t } = useTranslation("acquire");

  return (
    <div className="flex flex-col gap-md bg-linear-to-b from-brand-50 to-surface-card px-md py-xl sm:px-xl">
      <span className="inline-flex w-fit items-center gap-xs rounded-full border border-(--border-default) bg-surface-card px-sm py-1 text-xs-medium text-(--text-primary)">
        <span aria-hidden className="size-2 rounded-full bg-brand-600" />
        {t("dossier.step.chip", { index, total })}
      </span>

      <div className="flex flex-col gap-xs">
        {/* The step's own title, from the round — never our translation of it. */}
        <h1 className="display-xs-semibold text-(--text-primary)">{title}</h1>
        <p className="text-sm-regular text-(--text-secondary)">{description}</p>
      </div>
    </div>
  );
}
